import { Button, Card, CardContent, Stack, Typography } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  useScenarioQuery,
  useSimulationsQuery,
  useSimulationTypesQuery,
} from '../network/queries';
import { DataList, Loader } from '../components/common';
import { useSimulationLogDownloadMutation } from '../network/mutations';

function ScenarioViewPage() {
  const { t } = useTranslation();
  const { scenarioId } = useParams();
  const { isLoading: isLoadingScenario, data: scenario } =
    useScenarioQuery(scenarioId);
  const { isLoading: isLoadingSimulationTypes, data: simulationTypes } =
    useSimulationTypesQuery();
  const {
    isLoading: isLoadingSimulations,
    data: simulations,
    refetch: refetchSimulations,
  } = useSimulationsQuery(scenarioId);
  const { mutate, isPending: isSimulationLogDownloadPending } =
    useSimulationLogDownloadMutation();

  return isLoadingScenario ||
    !scenario ||
    isLoadingSimulationTypes ||
    !simulationTypes ||
    isLoadingSimulations ? (
    <Loader />
  ) : (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Typography variant="h4" color="primary.main">
            {scenario.name}
          </Typography>
          <Typography>{scenario.description}</Typography>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" color="primary.main">
              {t('pages.scenarios.view.configuration')}
            </Typography>
            <DataList
              items={[
                {
                  key: 'created_timestamp',
                  label: t('pages.scenarios.view.form.created'),
                  value: dayjs(scenario.runtime_input.created_timestamp).format(
                    'LL',
                  ),
                },
                {
                  key: 'updated_timestamp',
                  label: t('pages.scenarios.view.form.updated'),
                  value: dayjs(scenario.runtime_input.updated_timestamp).format(
                    'LL',
                  ),
                },
                {
                  key: 'simulation_type',
                  label: t('pages.scenarios.view.form.simulationType'),
                  value: simulationTypes.find(
                    (i) => i.id === scenario.simulation_type_id,
                  ).sim_type,
                },
              ]}
            />
            <Stack spacing={1}>
              <Typography variant="overline">
                {t('pages.scenarios.view.form.runtimeInput')}
              </Typography>
              <DataList
                items={[
                  {
                    key: 'runtime_input_start',
                    label: t('pages.scenarios.view.form.runtimeInputStart'),
                    value: dayjs(scenario.runtime_input.start).format('LL'),
                  },
                  {
                    key: 'runtime_input_duration',
                    label: t('pages.scenarios.view.form.runtimeInputDuration'),
                    value: scenario.runtime_input.duration,
                  },
                ]}
              />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="overline">
                {t('pages.scenarios.view.form.reporterInput')}
              </Typography>
              <DataList
                items={[
                  {
                    key: 'reporter_input_period',
                    label: t('pages.scenarios.view.form.reporterInputPeriod'),
                    value: scenario.reporter_input.period,
                  },
                ]}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="h6" color="primary.main">
              {t('pages.scenarios.view.form.simulations.header')}
            </Typography>
            {simulations && simulations.length ? (
              simulations.map((simulation) => (
                <Card key={simulation.id} variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <DataList
                        items={[
                          {
                            key: `${simulation.id}_created_timestamp`,
                            label: t(
                              'pages.scenarios.view.form.simulations.created',
                            ),
                            value: dayjs(simulation.created_timestamp).format(
                              'LL',
                            ),
                          },
                          {
                            key: `${simulation.id}_completed_timestamp`,
                            label: t(
                              'pages.scenarios.view.form.simulations.completed',
                            ),
                            value: dayjs(simulation.completed_timestamp).format(
                              'LL',
                            ),
                          },
                          {
                            key: `${simulation.id}_success_flag`,
                            label: t(
                              'pages.scenarios.view.form.simulations.success',
                            ),
                            value: t(
                              `common.boolean.${simulation.success_flag ? 'true' : 'false'}`,
                            ),
                          },
                        ]}
                      />
                      <Button
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon="file-arrow-down" />}
                        loading={isSimulationLogDownloadPending}
                        loadingPosition="start"
                        onClick={() => mutate({ simulationId: simulation.id })}
                        sx={{ alignSelf: 'center' }}
                      >
                        {t('pages.scenarios.view.form.simulations.download')}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))
            ) : (
              <>
                <Typography>
                  {t('pages.scenarios.view.form.simulations.empty')}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<FontAwesomeIcon icon="arrow-rotate-right" />}
                  loading={isLoadingSimulations}
                  loadingPosition="start"
                  onClick={refetchSimulations}
                  sx={{ alignSelf: 'center' }}
                >
                  {t('pages.scenarios.view.form.simulations.refresh')}
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

export default ScenarioViewPage;
